// Third Party Imports
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Toaster} from '@homeaway/react-toaster';
import TripBoardsProviderContainer from '../TripBoardsProviderContainer/TripBoardsProviderContainer';

// Relative Imports
import ChatbotActions from '../../actions/chatbotActions';
import ContactOwnerFormActions from '../../actions/contactOwnerFormActions';
import ListingActions from '../../actions/listingActions';
import ListingPageActions from '../../actions/listingPageActions';
import ReviewActions from '../../actions/reviewsActions';
import WindowActions from '../../actions/windowActions';
import EdapActions from '../../actions/edapActions';
import InquiryModalActions from '../../actions/inquiryModalActions';
import TripActions from '../../actions/tripActions';
import RouterActions from '../../actions/routerActions';
import TravelerActions from '../../actions/travelerActions';
import getListingPageData from '../../selectors/listingPage';

import windowResizeHandler from '../../utils/windowResizeHandler';
import errorHandler from '../../utils/errorHandler';
import {triggerErrorEdapEvent} from '../../utils/errorEvents';
import {writeCookie} from '../../utils/cookieHelpers';
import {getQueryParams} from '../../utils/queryStringHelper';
import ListingPageAll from './ListingPageAll';

/* global ha */
/* istanbul ignore next */
function mapStateToProps(state) {
    //  Use memoized selectors to compose what drives this container.
    return getListingPageData(state);
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
    // http://redux.js.org/docs/api/bindActionCreators.html
    // "you want to pass some action creators down to a component that isn't aware of Redux"
    // TODO: remember a better way to compose the actions.
    const chatbotActions = bindActionCreators(ChatbotActions, dispatch);
    const contactOwnerFormActions = bindActionCreators(ContactOwnerFormActions, dispatch);
    const listingActions = bindActionCreators(ListingActions, dispatch);
    const listingPageActions = bindActionCreators(ListingPageActions, dispatch);
    const reviewsActions = bindActionCreators(ReviewActions, dispatch);
    const windowActions = bindActionCreators(WindowActions, dispatch);
    const edapActions = bindActionCreators(EdapActions, dispatch);
    const inquiryModalActions = bindActionCreators(InquiryModalActions, dispatch);
    const tripActions = bindActionCreators(TripActions, dispatch);
    const routerActions = bindActionCreators(RouterActions, dispatch);
    const travelerActions = bindActionCreators(TravelerActions, dispatch);

    return {
        actions: {
            chatbotRequestToBook: chatbotActions.chatbotRequestToBook,
            checkIfFromListingPage: routerActions.checkIfFromListingPage,
            expandReviewClicked: reviewsActions.expandReviewClicked,
            inquiryClicked: listingPageActions.inquiryClicked,
            inquiryModalRequestToBook: inquiryModalActions.inquiryModalRequestToBook,
            inquiryModalSubmit: inquiryModalActions.inquirySubmit,
            logPriceSummaryEvent: listingPageActions.logPriceSummaryEvent,
            logTestExposure: edapActions.logTestExposure,
            logToServer: listingActions.logToServer,
            pageLoaded: listingActions.pageLoaded,
            primaryActionRendered: listingPageActions.primaryActionRendered,
            processServerSideEvaluatedTests: listingPageActions.processServerSideEvaluatedTests,
            scrollInquiryTop: contactOwnerFormActions.scrollTop,
            scrollReviewsTop: reviewsActions.scrollTop,
            setCivilCodeDisclaimerModalOpen: listingActions.setCivilCodeDisclaimerModalOpen,
            setLocationModalOpen: listingActions.setLocationModalOpen,
            setMapModalOpen: listingActions.setMapModalOpen,
            setToiModalOpen: listingActions.setToiModalOpen,
            setReviewsModalOpen: listingActions.setReviewsModalOpen,
            setReviewPageIndex: reviewsActions.setPageIndex,
            setStickyPosition: windowActions.setStickyPosition,
            setUserLoggedIn: travelerActions.setUserLoggedIn,
            setVirtualTourModalOpen: listingActions.setVirtualTourModalOpen,
            enterDatesTimerComplete: listingPageActions.enterDatesTimerComplete,
            triggerChatExitToast: chatbotActions.triggerChatExitToast,
            triggerEdapEvent: edapActions.triggerEdapEvent,
            triggerEdapPropertyDetailsMapPresented:
                edapActions.triggerEdapPropertyDetailsMapPresented,
            triggerEdapPropertyDetailsReviewsPresented:
                edapActions.triggerEdapPropertyDetailsReviewsPresented,
            triggerEdapPropertyDetailsAmenitiesPresented:
                edapActions.triggerEdapPropertyDetailsAmenitiesPresented,
            tripDetailsEdapCheck: tripActions.tripDetailsEdapCheck,
            updateTripDataCookie: listingActions.updateTripDataCookie,
            viewPriceDetailsClicked: listingPageActions.viewPriceDetailsClicked,
            windowScrolled: windowActions.windowScrolled,
            windowSizeChanged: windowActions.windowSizeChanged,
            triggerEdapPropertyDetailsReviewsDetailsSelected:
                edapActions.triggerEdapPropertyDetailsReviewsDetailsSelected,
            triggerEdapPropertyDetailsCancellationPolicyPresented:
                edapActions.triggerEdapPropertyDetailsCancellationPolicyPresented
        }
    };
}

@connect(
    mapStateToProps,
    mapDispatchToProps
)
@errorHandler
@windowResizeHandler
class ListingPageContainer extends Component {
    constructor(props) {
        super(props);

        this.timingConstructor = Date.now();
        this.loaded = false;
        if (typeof ha !== 'undefined') {
            ha.userTiming.mark('container.constructor');
        }
    }

    componentDidMount() {
        this.loaded = true;
        this.timingComponentDidMount = Date.now();
        ha.userTiming.mark('component.did.mount');
        this.logPar();

        const queryParams = getQueryParams(window.location.search);
        if (queryParams.noDates !== 'true' && queryParams.noGuests !== 'true') {
            this.setTripCookie();
        }

        this.props.actions.pageLoaded();
        this.checkForErrors();

        const affiliateCookieName = 'ha_affiliate_broker';
        const affiliateCookieValue = this.props.system.cookies[affiliateCookieName];
        if (affiliateCookieValue) {
            writeCookie(affiliateCookieName, affiliateCookieValue, false);
        }

        // Overriding the popstate handler to ensure no one else is handling it
        window.onpopstate = () => {};

        this.props.actions.processServerSideEvaluatedTests();

        setTimeout(() => {
            this.props.actions.enterDatesTimerComplete(true);
        }, 2500);
    }

    componentWillUnmount() {
        window.onpopstate = null;
    }

    /* eslint-disable complexity */
    logPar = () => {
        const {
            system,
            abacusTestData: {ha_pdp_mweb_perf_insights: mWebPerformanceTest}
        } = this.props;

        const renderTime = this.timingComponentDidMount - this.timingConstructor;
        const responseEndedAnalytics = ha.userTiming.getEntriesByName('responseEnded');
        const responseEnded = Math.ceil(
            responseEndedAnalytics.length ? responseEndedAnalytics[0].duration : 0
        );
        const indexInitializeAnalytics = ha.userTiming.getEntriesByName('index.initialize');
        const indexInitialize = Math.ceil(
            indexInitializeAnalytics.length ? indexInitializeAnalytics[0].startTime : 0
        );
        const filesDownloadedAndParsed = indexInitialize - responseEnded;

        const componentDidMountAnalytics = ha.userTiming.getEntriesByName('component.did.mount');
        const componentDidMount = Math.ceil(
            componentDidMountAnalytics.length ? componentDidMountAnalytics[0].startTime : 0
        );

        this.props.actions.logToServer({
            renderTime,
            filesDownloadedAndParsed,
            isMobile: system.isMobile,
            isDesktop: system.isDesktop,
            isTablet: system.isTablet,
            userAgent: system.userAgent,
            clientDesktopRenderTime: !system.isMobile ? renderTime : null,
            clientMobileRenderTime: system.isMobile ? renderTime : null,
            componentDidMount,
            performanceTestVariant: mWebPerformanceTest && mWebPerformanceTest.value
        });

        this.props.actions.primaryActionRendered();
    };

    /* eslint-enable complexity */

    setTripCookie = () => {
        const {actions} = this.props;

        actions.updateTripDataCookie();
    };

    checkForErrors() {
        const {bookingModalError, actions} = this.props;

        if (bookingModalError) {
            triggerErrorEdapEvent(actions.triggerEdapEvent, bookingModalError);
        }
    }

    render() {
        // if an existing prop is added to the named props, then it will not be included in rest, and should be explicitly added to ListingPageAll
        const {
            actions,
            listingPageComponent,
            dates,
            guests,
            siteName,
            isLoggedIn,
            brandName,
            ...rest
        } = this.props;

        return (
            <React.Fragment>
                <ListingPageAll
                    listingPageComponent={listingPageComponent}
                    brandName={brandName}
                    {...actions}
                    {...rest}
                />
                <Toaster />
                <TripBoardsProviderContainer
                    adultsCount={guests && guests.adultsCount}
                    childrenCount={guests && guests.childrenCount}
                    endDate={dates && dates.depart}
                    petIncluded={guests && guests.petIncluded}
                    startDate={dates && dates.arrive}
                    siteName={siteName}
                    isLoggedIn={isLoggedIn}
                    brand={brandName}
                    locale={this.props.system.locale}
                    setUserLoggedIn={actions.setUserLoggedIn}
                />
            </React.Fragment>
        );
    }
}

export default ListingPageContainer;
